import { SOFT_HYPHEN } from 'utils/text';
import type {
	App,
	Directive,
} from 'vue';

/**
 * Every <textarea> that currently has v-soft-hyphen-skipper applied
 */
const managed = new Set<HTMLTextAreaElement>();

/**
 * Previous caret / selection offsets used to infer direction
 */
let previousSelectionStart: number | null = null;
let previousSelectionEnd: number | null = null;

/**
 * Install *one* document-level listener lazily (first directive mount)
 */
function installGlobalListeners(): void {
	if (installGlobalListeners.installed) {
		return;
	}

	installGlobalListeners.installed = true;
	/**
	 * Fires after *every* selection change anywhere in the document.
	 * We act only when `document.activeElement` is one of our managed textareas.
	 */
	document.addEventListener(
		'selectionchange',
		() => {
			const activeElement = document.activeElement as HTMLTextAreaElement | null;

			if (
				!activeElement
				|| !managed.has(activeElement)
			) {
				return;
			}

			const selectionStart = (
				activeElement.selectionStart
				?? 0
			);
			const selectionEnd = (
				activeElement.selectionEnd
				?? 0
			);
			const { value } = activeElement;
			/**
			 * Decide whether to nudge the caret / selection
			 * @param previous – previous offset (null on first run)
			 * @param current – new offset from the browser
			 */
			const calculateSelection = (
				previous: number | null,
				current: number,
			): number => {
				/**
				 * First run or no move -> accept as-is
				 */
				if (
					previous === null
					|| previous === current
				) {
					return current;
				}

				const movingRight = current > previous;
				const movingLeft = current < previous;

				/**
				 * We only skip when the user moved exactly one code-unit
				 * and landed *just after* a soft-hyphen.
				 */
				if (
					movingRight
					&& current < value.length
					&& current - 1 === previous
					&& value.charAt(previous) === SOFT_HYPHEN
				) {
					return current + 1;
				}

				/**
				 * We only skip when the user moved exactly one code-unit
				 * and landed *on* a soft-hyphen.
				 */
				if (
					movingLeft
					&& current > 0
					&& current + 1 === previous
					&& value.charAt(current - 1) === SOFT_HYPHEN
				) {
					return current - 1;
				}

				/**
				 * No special case matched, just return the current offset
				 */
				return current;
			};

			const newSelectionStart = calculateSelection(
				previousSelectionStart,
				selectionStart,
			);
			const newSelectionEnd = calculateSelection(
				previousSelectionEnd,
				selectionEnd,
			);

			/**
			 * If the new selection start or end is different from the previous,
			 * we need to update the selection range.
			 */
			if (
				newSelectionStart !== selectionStart
				|| newSelectionEnd !== selectionEnd
			) {
				/**
				 * Update the previous selection start and end for the next run
				 */
				previousSelectionStart = newSelectionStart;
				previousSelectionEnd = newSelectionEnd;
				activeElement.setSelectionRange(
					newSelectionStart,
					newSelectionEnd,
				);
			}
		},
	);
}

installGlobalListeners.installed = false;

/**
 * Directive to skip soft-hyphens in textareas.
 * It listens to selection changes and adjusts the caret position
 * to skip over soft-hyphens when the user navigates through the text.
 */
export const directive: Directive<HTMLTextAreaElement, void> = {
	mounted(element) {
		if (!(element instanceof HTMLTextAreaElement)) {
			console.warn('v-soft-hyphen-skipper should be used on a <textarea>.');
			return;
		}

		managed.add(element);
		installGlobalListeners();
	},
	unmounted(element) {
		managed.delete(element);
	},
};

/**
 * Vue plugin to install the v-soft-hyphen-skipper directive using `app.use(...)`.
 * This allows the directive to be used in any Vue component.
 */
const plugin = {
	install(app: App): void {
		app.directive(
			'softhyphen-skipper',
			directive,
		);
	},
	directive,
};

export default plugin;
